import { useSyncExternalStore } from 'react'
import { orpc } from '@/lib/orpc-client'

/**
 * Project members for the editor (board 9s8r): the `@` menu and the mention
 * chips read them synchronously, and the chips render inside BlockNote node
 * views — so this is a tiny external store keyed by project instead of a
 * React context. `orpc.members.list`, cached per project for MEMBERS_TTL_MS.
 */

export interface MentionMember {
  userId: string
  name: string | null
  email: string
  avatarUrl: string | null
  role: 'owner' | 'admin' | 'member'
  disabledAt: string | null
}

const MEMBERS_TTL_MS = 5 * 60_000
const EMPTY: MentionMember[] = []

interface Entry {
  members: MentionMember[]
  loadedAt: number
  inflight: Promise<MentionMember[]> | null
}

const entries = new Map<string, Entry>()
const listeners = new Set<() => void>()

function emit() {
  for (const l of listeners) l()
}

/** Members of `projectId` as last loaded (empty until then). Never disabled members. */
export function getMembers(projectId: string): MentionMember[] {
  return entries.get(projectId)?.members ?? EMPTY
}

/** Fetch (or refresh a stale cache); concurrent calls share one request. */
export function ensureMembersLoaded(projectId: string, force = false): Promise<MentionMember[]> {
  const entry = entries.get(projectId)
  if (entry?.inflight) return entry.inflight
  if (entry && !force && Date.now() - entry.loadedAt < MEMBERS_TTL_MS)
    return Promise.resolve(entry.members)
  const next: Entry = entry ?? { members: EMPTY, loadedAt: 0, inflight: null }
  next.inflight = orpc.members
    .list({ projectId })
    .then((res) => {
      next.members = res.members.map((m) => ({
        userId: m.userId,
        name: m.name,
        email: m.email,
        avatarUrl: m.avatarUrl,
        role: m.role,
        disabledAt: m.disabledAt,
      }))
      next.loadedAt = Date.now()
      return next.members
    })
    .catch((err) => {
      console.warn('[members-store] members.list failed', err)
      return next.members
    })
    .finally(() => {
      next.inflight = null
      emit()
    })
  entries.set(projectId, next)
  return next.inflight
}

export function subscribeMembers(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** The project's members, re-rendering when the cache changes. */
export function useMembers(projectId: string): MentionMember[] {
  return useSyncExternalStore(
    subscribeMembers,
    () => getMembers(projectId),
    () => EMPTY
  )
}

/** True once members.list has answered for this project (a failed request never counts). */
export function membersLoaded(projectId: string): boolean {
  return (entries.get(projectId)?.loadedAt ?? 0) > 0
}

/**
 * One member by id. `member` is null while the list is loading *and* for a
 * former member — `loaded` tells them apart, so a chip never flashes
 * "former member" on open or when the request fails.
 */
export function useMember(
  projectId: string,
  userId: string
): { member: MentionMember | null; loaded: boolean } {
  const member = useSyncExternalStore(
    subscribeMembers,
    () => getMembers(projectId).find((m) => m.userId === userId) ?? null,
    () => null
  )
  const loaded = useSyncExternalStore(
    subscribeMembers,
    () => membersLoaded(projectId),
    () => false
  )
  return { member, loaded }
}
